import { Badge, Box, DarkMode, HStack, Link, ListItem, OrderedList, Spacer, Table, TableContainer, Tbody, Td, Tooltip, Tr, useDisclosure } from "@chakra-ui/react";
import { Button } from "../common/styled";
import { CopyIconButton } from "../../../fix-search/components/CopyButton/CopyIconButton";
import { NotificationToast } from "./NotificationToast";
import ReactSelect from "react-select";
import { customStyles } from "../common/Select";
import { FormLabel } from "../common/FormLabel";
import { CollapsableCodeBlock} from "../common/CodeBlock";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

const onlyUnique = (value, index, self) => (self.indexOf(value) === index)

const knownErrorKeys = new Set(['id', 'message', 'level', 'stackTrace', 'stack', 'collapsedIds'])

const dateToString = (date) => new Date(date).toString()

const CollapsableListOfDates = ({ dates }) => {
    const { isOpen, onToggle } = useDisclosure()

    return <OrderedList>
        <ListItem>{dateToString(dates[0])}</ListItem>
        {dates.length > 2 && <Tooltip
            label={isOpen ? 'Hide' : 'Show all'}
        >
            <Link onClick={onToggle} color='gray.400' _hover={{ textDecoration: 'underline dashed' }}>{
                isOpen ? <>{dates.slice(1, dates.length-1).map(dateToString).map(dateStr => <ListItem>{dateStr}</ListItem>)}</>
                : <>...</>
            }</Link>
        </Tooltip>}
        <ListItem value={dates.length}>{dateToString(dates[dates.length-1])}</ListItem>
    </OrderedList>
} 

const NotificationFieldValue = ({name, value}) => {
    if (name === 'timestamp') {
        return dateToString(value)
    }
    if (name === 'timestamps') {
        return <CollapsableListOfDates dates={value} />
    }

    if (typeof value === 'object') {
        if (Object.keys(value).length) {
            return <CollapsableCodeBlock>{JSON.stringify(value, null, 2)}</CollapsableCodeBlock>
        } else {
            // Sometimes error objects has only prototype keys
            return <CollapsableCodeBlock>{value.toString()}</CollapsableCodeBlock> 
        }
    }

    if (name === 'socketMessage') {
        return <CollapsableCodeBlock>{value}</CollapsableCodeBlock> 
    }

    return value
}

const Notification = memo(({notification, clearById}) => {
    const clear = useCallback(() => {
        notification.collapsedIds?.forEach(clearById)
        clearById(notification.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const notificationWithoutId = { ...notification }
    notificationWithoutId.id = undefined
    const notificationTextToCopy = JSON.stringify(notificationWithoutId, null, 2)
    const notificationFields = Object.keys(notification).filter((key) => (!knownErrorKeys.has(key) && notification[key]))
    return <div>
        <NotificationToast
            onClose={clear}
            title={notification.message}
            status={notification.level}
            count={notification.collapsedIds ? (notification.collapsedIds.length + 1) : undefined}
        >
            <Box>
                <DarkMode>
                    <CopyIconButton
                        copyText={notificationTextToCopy}
                        tooltip='Copy Message'
                        size='xs'
                        position='absolute'
                        right={9}
                        variant='ghost'
                        top={2} 
                    />
                </DarkMode>
                {notificationFields.length > 0 &&
                    <TableContainer width='fit-content'>
                        <Box
                            border='1px solid var(--chakra-colors-whiteAlpha-400)'
                            borderRadius='5px'
                            overflowX='auto'
                            paddingY={1}
                            paddingX={3}
                        >
                            <Table
                                fontFamily='var(--code-font-family)'
                                size='sm'
                            >
                                <Tbody>
                                    {notificationFields.sort().map((fieldName, index) => (
                                        <Tr>
                                            <Td
                                                color='gray.400'
                                                borderColor='whiteAlpha.400'
                                                borderBottom={(index === (notificationFields.length - 1)) ? 'none' : undefined}
                                            >
                                                {fieldName}
                                            </Td>
                                            <Td
                                                borderColor='whiteAlpha.400'
                                                borderBottom={(index === (notificationFields.length - 1)) ? 'none' : undefined}
                                            >
                                                <NotificationFieldValue name={fieldName} value={notification[fieldName]} />
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </TableContainer>
                }
                {(notification.stackTrace || notification.stack) &&
                    <CollapsableCodeBlock
                        marginTop={4}
                    >
                        {notification.stackTrace || notification.stack}
                    </CollapsableCodeBlock>
                }
            </Box>
        </NotificationToast>
    </div>
})

export const Notifications = memo(({ 
    notifications,
    setNotifications,
    onClearAll,
    notificationFilterBySessionId,
    setNotificationFilterBySessionId
}) => {

    const clearAll = () => {
        setNotifications([])
        onClearAll()
    }

    const clearById = useCallback((id) => {
        setNotifications((notifications) => {
            var newNotifications = notifications.filter(notificationMsg => notificationMsg.id !== id)
            
            if (!newNotifications.length) {
                onClearAll()
            }

            return newNotifications
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [sessionsWithNotifications, setSessionsWithNotifications] = useState([])
  
    useEffect(() => {
        const sessions = notifications.filter((notification) => notification.sessionID).map((notification) => notification.sessionID).filter(onlyUnique)
        if (JSON.stringify(sessions) !== JSON.stringify(sessionsWithNotifications)) {
            setSessionsWithNotifications(sessions)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications])
  
    const sessionOptions = useMemo(
        () => sessionsWithNotifications.map((sessionID) => ({ value: sessionID, label: sessionID }))
    , [sessionsWithNotifications])

    const filteredNotifications = useMemo(() => 
        notifications
            .filter((notification) => 
                (!notificationFilterBySessionId || notificationFilterBySessionId === notification.sessionID))
    , [notifications, notificationFilterBySessionId])

    const filteredAndReducedNotifications = useMemo(() => 
        filteredNotifications
            .reduce((reduced, notification, index) => {
                if (index === 0) {
                    reduced.push(notification)
                } else {
                    let previousNotification = reduced[reduced.length-1]
                    const previousNotificationWithoutTimestamp = { ...previousNotification }
                    previousNotificationWithoutTimestamp.timestamps = undefined
                    previousNotificationWithoutTimestamp.timestamp = undefined
                    previousNotificationWithoutTimestamp.id = undefined
                    previousNotificationWithoutTimestamp.collapsedIds = undefined

                    const currentNotificationWithoutTimestamp = { ...notification }
                    currentNotificationWithoutTimestamp.timestamp = undefined
                    currentNotificationWithoutTimestamp.id = undefined

                    if (JSON.stringify(previousNotificationWithoutTimestamp) === JSON.stringify(currentNotificationWithoutTimestamp)) {
                        if (!previousNotification.collapsedIds) {
                            // reuse 'previousNotificationWithoutTimestamp' object
                            previousNotificationWithoutTimestamp.timestamps = [ previousNotification.timestamp ]
                            previousNotificationWithoutTimestamp.id = previousNotification.id
                            previousNotificationWithoutTimestamp.collapsedIds = []
                            previousNotification = previousNotificationWithoutTimestamp
                            
                            // replace previousNotification with new object, to avoid mutation of original list
                            reduced[reduced.length-1] = previousNotification
                        }
                        previousNotification.timestamps.push(notification.timestamp)
                        previousNotification.collapsedIds.push(notification.id)
                    } else {
                        reduced.push(notification)
                    }
                }

                return reduced
        }, [])
    , [filteredNotifications])

    return (
        <>
            <HStack>
                <FormLabel
                    margin={0}
                >Filter by </FormLabel>
                <ReactSelect
                    placeholder='Session ID'
                    styles={customStyles}
                    isClearable
                    value={notificationFilterBySessionId ? { value: notificationFilterBySessionId, label: notificationFilterBySessionId } : null}
                    onChange={(value) => setNotificationFilterBySessionId(value ? value.value : null)}
                    options={sessionOptions}
                />
                {notificationFilterBySessionId
                    ? <Badge fontFamily='var(--code-font-family)' fontSize='1em'>{filteredNotifications.length}</Badge>
                    : <></>
                }
                <Spacer/>
                <Button onClick={clearAll}>Clear All</Button>
            </HStack>
            {filteredAndReducedNotifications
                .map((notification) =>{
                    return <Notification 
                        key={`${notification.id}_${notification.collapsedIds?.length}`}
                        notification={notification}
                        clearById={clearById}
                    />}
            )}
        </>
    )
})